import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { 
  MdReceipt, 
  MdEvent, 
  MdAssignment, 
  MdSpeed, 
  MdTimelapse, 
  MdCheckCircle,
  MdInfo
} from "react-icons/md";
import usePageTitle from "../../utils/hooks/usePageTitle";
import { getMyCompanySubscriptions } from "../../utils/api/endpoints/dashboard/dashboardApi";
import moment from "moment";
import { FiRefreshCw } from "react-icons/fi";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4 }
  }
};

const SubscriptionHistory: React.FC = () => {
  usePageTitle("Subscription Cycles");
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSubscriptions = async () => {
    setLoading(true);
    const data = await getMyCompanySubscriptions();
    setSubscriptions(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const getStatus = (start: string, end: string) => {
    const now = moment();
    const startDate = moment(start);
    const endDate = moment(end);

    if (now.isBefore(startDate)) return { label: "Upcoming", color: "bg-blue-100 text-blue-800" };
    if (now.isAfter(endDate)) return { label: "Expired", color: "bg-gray-100 text-gray-800" };
    return { label: "Active", color: "bg-green-100 text-green-800" };
  };

  return (
    <motion.div
      className="min-h-screen bg-slate-50 p-6 md:p-8 max-w-7xl mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Subscription Cycles</h1>
        <p className="mt-2 text-sm text-gray-600">
          View your company's subscription cycles and resource allocations.
        </p>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center">
          <FiRefreshCw className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <MdReceipt className="mx-auto h-16 w-16 text-gray-300" />
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No subscriptions found</h3>
          <p className="mt-2 text-gray-500">Your company doesn't have any subscription history yet.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {subscriptions.map((sub, index) => {
            const status = getStatus(sub.start_date, sub.end_date);
            const isActive = status.label === "Active";

            return (
              <motion.div
                key={sub.subscription_id}
                variants={itemVariants}
                onClick={() => navigate(`/dashboard/subscription/${sub.subscription_id}`)}
                className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition-all hover:shadow-lg hover:-translate-y-1 cursor-pointer flex flex-col ${
                  isActive ? "ring-2 ring-blue-500 ring-opacity-20 border-blue-100" : "border-gray-100"
                }`}
              >
                {/* Header */}
                <div className={`px-5 py-4 flex flex-col gap-3 ${
                  isActive ? "bg-blue-50/30" : "bg-gray-50/50"
                }`}>
                  <div className="flex items-start justify-between">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl flex-shrink-0 ${
                      isActive ? "bg-blue-600 text-white shadow-md shadow-blue-100" : "bg-gray-200 text-gray-600"
                    }`}>
                      <MdReceipt className="h-5 w-5" />
                    </div>
                    <div className="flex items-center gap-2">
                      {status.label === "Active" && (
                        <span className="text-[10px] font-black text-blue-600/60 uppercase tracking-tighter">
                          {moment(sub.end_date).diff(moment(), "days")} days left
                        </span>
                      )}
                      {status.label === "Upcoming" && (
                        <span className="text-[10px] font-black text-amber-600/60 uppercase tracking-tighter">
                          In {moment(sub.start_date).diff(moment(), "days")} days
                        </span>
                      )}
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900 leading-tight" title={sub.subscription_name || `${sub.subscription_type} Plan`}>
                      {sub.subscription_name || `${sub.subscription_type?.charAt(0).toUpperCase() + sub.subscription_type?.slice(1)} Plan`}
                    </h2>
                    <div className="flex items-center gap-1.5 mt-1 text-[11px] text-gray-400 font-medium">
                      <MdEvent className="h-3.5 w-3.5" />
                      {moment(sub.start_date).format("MMM D")} - {moment(sub.end_date).format("MMM D, YYYY")}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="px-5 py-5 flex-1 flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Comprehensive Quota */}
                    <div className="rounded-xl border border-gray-50 bg-gray-50/50 p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Comp. Quota</span>
                        <MdSpeed className="h-3.5 w-3.5 text-indigo-400" />
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-black text-gray-900">{sub.comprehensive_runs_quota}</span>
                        <span className="text-[10px] font-bold text-gray-400">UNITS</span>
                      </div>
                    </div>

                    {/* Quick Run Quota */}
                    <div className="rounded-xl border border-gray-50 bg-gray-50/50 p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Quick Quota</span>
                        <MdTimelapse className="h-3.5 w-3.5 text-emerald-400" />
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl font-black text-gray-900">{sub.quick_runs_quota}</span>
                        <span className="text-[10px] font-bold text-gray-400">UNITS</span>
                      </div>
                    </div>
                  </div>

                  {isActive && (
                    <div className="mt-auto flex items-center gap-2 text-[10px] font-bold text-blue-600 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100">
                      <MdInfo className="h-3.5 w-3.5" />
                      CURRENT ACTIVE PLAN
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default SubscriptionHistory;
